/*
 * Назначение: выбор провайдера нейросети для конкретной модели.
 * Функционал: список провайдеров, заведённых в «Интеграциях», плюс вариант
 * «любой доступный» (пустое значение — модель пробуется у всех по порядку
 * приоритета). Выключенные провайдеры помечены: сразу видно, почему модель
 * может не отвечать. Списка провайдеров в коде нет — он приходит из админки.
 */

import { useQuery } from "@tanstack/react-query";

import { integrationsApi, type AiProvider } from "../../../lib/admin-api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

/** Пункт «любой доступный»: пустую строку Radix в качестве значения не берёт. */
export const ANY_PROVIDER = "__any__";

export const ANY_PROVIDER_LABEL = "Любой доступный";

/** Провайдеры из «Интеграций». Кешируются на все вкладки настроек. */
export function useAiProviders(): AiProvider[] {
  const { data } = useQuery({
    queryKey: ["ai-providers"],
    queryFn: () => integrationsApi.getAiProviders(),
  });
  return data?.providers ?? [];
}

/** Как показать привязку модели к провайдеру в списке. */
export function providerLabel(providers: AiProvider[], providerId: string): string {
  if (!providerId) return ANY_PROVIDER_LABEL;
  const found = providers.find((p) => String(p.id ?? "") === providerId);
  if (!found) return "Провайдер удалён";
  return found.enabled ? found.name : `${found.name} (выключен)`;
}

export function ProviderSelect({
  value,
  providers,
  onChange,
  className,
}: {
  value: string;
  providers: AiProvider[];
  onChange: (providerId: string) => void;
  className?: string;
}) {
  return (
    <Select
      value={value || ANY_PROVIDER}
      onValueChange={(v) => onChange(v === ANY_PROVIDER ? "" : v)}
    >
      <SelectTrigger className={className} aria-label="Провайдер модели">
        <SelectValue placeholder={ANY_PROVIDER_LABEL} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY_PROVIDER}>{ANY_PROVIDER_LABEL}</SelectItem>
        {providers
          .filter((p) => String(p.id ?? ""))
          .map((p) => (
            <SelectItem key={String(p.id)} value={String(p.id)}>
              {p.enabled ? p.name : `${p.name} (выключен)`}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  );
}
